/*
 * AMASS Trade lead-capture forms (US-011)
 * ----------------------------------------------------------------------------
 * Progressive-enhancement controller for the two Trade forms rendered by
 * sections/trade-forms.liquid:
 *   - data-trade-form="pos"     → REQUEST POS MATERIALS
 *   - data-trade-form="contact" → GET IN TOUCH (wholesale inquiry)
 *
 * Both forms are native Shopify `{% form 'contact' %}` forms. They post to
 * /contact as a real page navigation, which is the only path on which Shopify's
 * storefront spam protection can issue its token — a background fetch() to
 * /contact is answered with 400 "Missing CAPTCHA token" and the notification
 * email is silently dropped (that is exactly what happened from launch until
 * 2026-09-14: every trade lead reached Klaviyo but never hello@amass.com).
 *
 * On submit this script:
 *   1. Validates client-side — required fields + email format — and renders
 *      inline errors (.trade-error) with aria-invalid; focuses the first error.
 *      Invalid → the native submit is cancelled.
 *   2. Copies the checked recipe boxes into the hidden contact[Recipes] field so
 *      they land in the Shopify email as one line.
 *   3. Fires a CLIENT-SIDE Klaviyo identify + track using the onsite object
 *      (window.klaviyo, new API; window._learnq legacy fallback). The onsite JS
 *      is loaded globally by snippets/analytics.liquid with the AMASS company id
 *      (settings.klaviyo_company_id). Creates a trade-lead PROFILE + EVENT.
 *   4. Lets the native form submission proceed once the Klaviyo call has
 *      settled (or after a short cap, so tracking can never block the lead).
 *      Shopify emails the store notification address and redirects back to this
 *      page with contact_posted=true; the section renders the success state
 *      server-side and this script scrolls it into view.
 *
 * Without JS the forms still work end to end (native required/email checks,
 * native submit); only the recipe line and the Klaviyo event are skipped.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var KLAVIYO_WAIT_MS = 800;

  // Stable key for a field: data-trade-key (set in the section) or the raw name.
  function keyOf(input) {
    return input.getAttribute('data-trade-key') || input.getAttribute('name') || '';
  }

  function fieldLabel(input) {
    var wrap = input.closest('.trade-field') || input.closest('.trade-fieldset');
    var label = wrap && wrap.querySelector('.trade-label');
    var text = label ? label.textContent : (keyOf(input) || 'This field');
    return text.replace(/\*/g, '').trim();
  }

  function errorEl(input) {
    var form = input.closest('form');
    return form && form.querySelector('[data-trade-error-for="' + keyOf(input) + '"]');
  }

  function showError(input, message) {
    input.setAttribute('aria-invalid', 'true');
    var el = errorEl(input);
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
  }

  function clearError(input) {
    input.removeAttribute('aria-invalid');
    var el = errorEl(input);
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function validate(form) {
    var firstInvalid = null;
    var requireds = form.querySelectorAll('[data-trade-required]');

    requireds.forEach(function (input) {
      clearError(input);
      var value = (input.value || '').trim();
      if (!value) {
        showError(input, fieldLabel(input) + ' is required.');
        if (!firstInvalid) firstInvalid = input;
      }
    });

    // Email format (only when the field has a value; emptiness handled above).
    form.querySelectorAll('[data-trade-email-field]').forEach(function (input) {
      var value = (input.value || '').trim();
      if (value && !EMAIL_RE.test(value)) {
        showError(input, 'Enter a valid email address.');
        if (!firstInvalid) firstInvalid = input;
      }
    });

    // Recipe checkboxes: the POS form requires at least one selection
    // (fieldset carries data-trade-recipes-required; mirrors live amass.com/trade).
    var recipeSet = form.querySelector('[data-trade-recipes-required]');
    if (recipeSet) {
      var recipesErr = form.querySelector('[data-trade-error-for="recipes"]');
      var anyChecked = recipeSet.querySelectorAll('input[name="recipes"]:checked').length > 0;
      if (!anyChecked) {
        if (recipesErr) { recipesErr.textContent = 'Select at least one recipe.'; recipesErr.hidden = false; }
        if (!firstInvalid) firstInvalid = recipeSet.querySelector('input[name="recipes"]');
      } else if (recipesErr) {
        recipesErr.textContent = '';
        recipesErr.hidden = true;
      }
    }

    if (firstInvalid) {
      try { firstInvalid.focus(); } catch (e) {}
    }
    return !firstInvalid;
  }

  function collect(form) {
    var data = { recipes: [] };
    form.querySelectorAll('input, select, textarea').forEach(function (input) {
      if (!input.name) return;
      if (input.type === 'checkbox') {
        if (input.checked && input.name === 'recipes') data.recipes.push(input.value);
        return;
      }
      if (input.type === 'hidden') return;
      data[keyOf(input)] = (input.value || '').trim();
    });
    return data;
  }

  // Mirror the checked recipes into contact[Recipes] so Shopify's email carries them.
  function syncRecipes(form, data) {
    var target = form.querySelector('[data-trade-recipes-target]');
    if (target) target.value = (data.recipes || []).join(', ');
  }

  // "Full name" → Klaviyo first/last. First word is the first name, the rest is
  // the last name ("Mary Ann Lee" → "Mary" / "Ann Lee"). A single word is a
  // first name only. Case is left as typed.
  function splitName(full) {
    var parts = String(full || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return { first: '', last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
  }

  // --- Klaviyo client-side identify + track (onsite JS, no server key) ---------
  // Resolves when the calls have been handed to the onsite object (or at once
  // when it is absent). Never rejects, never blocks the submit.
  function klaviyoCapture(eventName, data) {
    return new Promise(function (resolve) {
      try {
        var profile = {};
        if (data.email) profile.$email = data.email;
        if (data.name) {
          var n = splitName(data.name);
          if (n.first) profile.$first_name = n.first;
          if (n.last) profile.$last_name = n.last;
        }
        if (data.company || data.venue) profile.$organization = data.company || data.venue;
        if (data.phone) profile.$phone_number = data.phone;

        var props = {
          form: eventName,
          venue: data.venue || '',
          company: data.company || '',
          business_type: data.business_type || '',
          material_type: data.material_type || '',
          recipes: (data.recipes || []).join(', '),
          message: data.message || '',
          source: 'Trade page (US-011)'
        };

        var k = window.klaviyo;
        if (k && typeof k.identify === 'function' && typeof k.track === 'function') {
          // Modern onsite API returns promises; wait for track to be sent.
          var chain = profile.$email ? Promise.resolve(k.identify(profile)) : Promise.resolve();
          chain
            .then(function () { return k.track(eventName, props); })
            .then(function () { resolve(true); }, function () { resolve(false); });
          return;
        }
        if (k && typeof k.push === 'function') {
          if (profile.$email) k.push(['identify', profile]);
          k.push(['track', eventName, props]);
          resolve(true);
          return;
        }
        if (window._learnq && typeof window._learnq.push === 'function') {
          // Legacy onsite API.
          if (profile.$email) window._learnq.push(['identify', profile]);
          window._learnq.push(['track', eventName, props]);
          resolve(true);
          return;
        }
      } catch (e) { /* never block the submit on tracking */ }
      resolve(false);
    });
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve(false); } }, ms);
      promise.then(function (v) { if (!done) { done = true; clearTimeout(t); resolve(v); } });
    });
  }

  var LAST_FORM_KEY = 'amassTradeLastForm';

  function rememberSubmitted(form) {
    try { sessionStorage.setItem(LAST_FORM_KEY, form.id); } catch (e) {}
  }

  // Which trade form did this visitor just submit? Shopify's redirect keeps the
  // form action's #fragment (action="/contact#<form id>"); sessionStorage is the
  // fallback if a browser drops it. Null when unknown.
  function submittedFormId() {
    var hash = (window.location.hash || '').replace(/^#/, '');
    if (hash && document.getElementById(hash) && document.getElementById(hash).matches('[data-trade-form]')) {
      return hash;
    }
    try {
      var stored = sessionStorage.getItem(LAST_FORM_KEY);
      if (stored && document.getElementById(stored)) return stored;
    } catch (e) {}
    return null;
  }

  // Shopify renders form.posted_successfully? / form.errors into every contact
  // form on the page, not just the one that was sent. Strip the server status
  // from the forms that were not submitted. Returns true if this form keeps it.
  function scopeServerStatus(form) {
    var statuses = form.querySelectorAll('[data-trade-server-status]');
    if (!statuses.length) return false;
    var target = submittedFormId();
    if (target && target !== form.id) {
      statuses.forEach(function (el) { el.parentNode.removeChild(el); });
      return false;
    }
    return true;
  }

  // After Shopify redirects back with the success state rendered server-side.
  function revealPosted(form) {
    if (!scopeServerStatus(form)) return false;
    var posted = form.querySelector('[data-trade-posted]');
    if (!posted) return false;
    try { sessionStorage.removeItem(LAST_FORM_KEY); } catch (e) {}
    form.classList.add('is-posted');
    var wrap = form.closest('.trade-form-wrap');
    if (wrap) wrap.classList.add('is-submitted');
    try {
      posted.scrollIntoView({ block: 'center' });
      posted.focus();
    } catch (e) {}
    return true;
  }

  function bind(form) {
    if (form.__tradeBound) return;
    form.__tradeBound = true;

    // JS owns validation from here; native bubbles stay on when JS is off.
    form.setAttribute('novalidate', '');

    if (revealPosted(form)) return;

    // Clear an error as the user fixes the field.
    form.addEventListener('input', function (e) {
      var t = e.target;
      if (t && (t.matches('[data-trade-required]') || t.matches('[data-trade-email-field]'))) {
        if (t.getAttribute('aria-invalid') === 'true') clearError(t);
      }
      if (t && t.name === 'recipes' && t.checked) {
        var recipesErr = form.querySelector('[data-trade-error-for="recipes"]');
        if (recipesErr) { recipesErr.textContent = ''; recipesErr.hidden = true; }
      }
    });

  }

  // First pass of a submit: validate + Klaviyo, then release a second submit.
  // Registered ONCE at document level in the CAPTURE phase so it runs before
  // Shopify's spam-protection listener on the form. That listener
  // (storefront-forms-hcaptcha) calls preventDefault() and runs the hCaptcha →
  // real POST flow on EVERY submit event without checking defaultPrevented, so
  // if it saw the first pass it would post an unvalidated form and then post
  // again on our release. stopImmediatePropagation() keeps it to one pass.
  function onSubmitCapture(e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('[data-trade-form]')) return;
    if (form.__tradeReleasing) {
      form.__tradeReleasing = false;
      return; // second pass: let Shopify's captcha handler + native submit run
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    if (!validate(form)) return;

    var eventName = form.getAttribute('data-klaviyo-event') || 'Trade Lead Submitted';
    var data = collect(form);
    syncRecipes(form, data);

    var submit = form.querySelector('.trade-submit');
    if (submit) { submit.setAttribute('aria-disabled', 'true'); submit.disabled = true; }

    withTimeout(klaviyoCapture(eventName, data), KLAVIYO_WAIT_MS).then(function () {
      rememberSubmitted(form);
      form.__tradeReleasing = true;
      if (submit) submit.disabled = false; // disabled controls are dropped from the POST
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(); // fires a submit event → Shopify's captcha flow → POST
      } else {
        form.submit(); // Shopify's script replaces form.submit with its captcha flow
      }
    });
  }

  var captureBound = false;
  function bindCapture() {
    if (captureBound) return;
    captureBound = true;
    document.addEventListener('submit', onSubmitCapture, true);
  }

  function init(root) {
    bindCapture();
    (root || document).querySelectorAll('[data-trade-form]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor: re-bind when the section re-renders.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
